import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';
import { ValidatorForm } from 'react-form-validator-core';
import { useHistory, useLocation } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import BACKGROUND_IMAGE from 'core/assets/images/bg3.jpg';

import useUser from 'core/hooks/useUser';
import useErrorHandlers from 'core/hooks/useErrorHandlers';
import { orderManagementPath } from 'onramp/api/navigation';

import SplitPageContainer from 'core/ui/components/SplitPageContainer';
import {
  Container,
  Flex,
  Box,
  Label,
  FormGroup,
  Button,
  ValidatedInput,
  RouterLink,
  Header,
  Hr,
} from 'core/ui/atoms';
import { SIGN_IN, SIGN_OUT } from 'core/api/apollo/mutations';
import { ERROR_MESSAGES } from 'core/api/constants';
import cacheUpdater, { updateUserQuery } from 'core/api/cache/updaters';
import { isGuestEmail } from 'onramp/helpers';
import { session } from 'core/api/storage';
import client from 'core/api/apollo/apolloClient';
import useReferralCode from 'core/hooks/useReferralCode';

function LoginPage() {
  const location = useLocation();
  const history = useHistory();
  const { clearReferralCode } = useReferralCode();
  const { currentUser } = useUser();
  const { displayApolloResponseErrors, handleGenericApolloError } = useErrorHandlers();
  const [formData, setFormData] = useState({ email: '', password: '', rememberMe: true });
  const [isProcessing, setIsProcessing] = useState(false);
  const { email, password, rememberMe } = formData;

  const onChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const onComplete = () => {
    session.clear();
    return client.resetStore().then(() => {
      clearReferralCode();
    });
  };

  const [logout] = useMutation(SIGN_OUT, {
    variables: { input: {} },
    onCompleted: onComplete,
    onError: onComplete,
  });

  useEffect(() => {
    if (currentUser && isGuestEmail(currentUser.email)) {
      logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const onLogin = ({ signIn: { errors, signedIn, user } }) => {
    const destination = location && location.state && location.state.destination;

    if (signedIn) {
      if (isEmpty(user?.subscription?.subscriptionQuantities)) {
        history.push(orderManagementPath());
      } else {
        history.push(destination || '/');
      }
    } else if (errors && errors.length) {
      displayApolloResponseErrors(errors);
      setFormData({ ...formData, password: '' });
      setIsProcessing(false);
    }
  };

  const [signIn] = useMutation(SIGN_IN, {
    variables: { input: { email, password, rememberMe } },
    update: cacheUpdater('signIn', updateUserQuery),
    onCompleted: onLogin,
    onError: handleGenericApolloError,
  });

  const onSubmit = (e) => {
    e.preventDefault();
    setIsProcessing(true);
    signIn().catch(() => setIsProcessing(false));
  };

  return (
    <SplitPageContainer noPadding image={BACKGROUND_IMAGE}>
      <Container>
        <Flex>
          <Box md={4} mdOffset={1}>
            <WelcomeHeader data-test-welcome-text>Login</WelcomeHeader>
            <Hr />
            <Form onSubmit={onSubmit} noValidate instantValidate={false}>
              <FormGroup>
                <InputLabel required>Email</InputLabel>
                <ValidatedInputText
                  type="email"
                  name="email"
                  placeholder="enter email id"
                  onChange={onChange}
                  value={email}
                  validators={['required', 'isEmail']}
                  errorMessages={[ERROR_MESSAGES.required, ERROR_MESSAGES.email]}
                />
              </FormGroup>

              <FormGroup>
                <InputLabel required>Password</InputLabel>
                <PasswordInputWrap>
                  <ValidatedInputText
                    type="password"
                    name="password"
                    placeholder="enter password"
                    onChange={onChange}
                    value={password}
                    validators={['required']}
                    errorMessages={[ERROR_MESSAGES.required]}
                  />
                </PasswordInputWrap>
              </FormGroup>

              <SubmitButtonCont>
                <FormGroup>
                  <SignInButton
                    type="submit"
                    btn-primary
                    btn-lg
                    data-test-sign-in
                    disabled={isProcessing}
                  >
                    Sign In
                  </SignInButton>
                </FormGroup>
              </SubmitButtonCont>
            </Form>
            <ForgotLink to="/forgot-password">Forgot my password / Dont have one yet ?</ForgotLink>
            <CreateAccountLink to="/">No account? Create one now</CreateAccountLink>
          </Box>
        </Flex>
      </Container>
    </SplitPageContainer>
  );
}

const WelcomeHeader = styled(Header)`
  margin-top: auto;
  font-weight: 400;
  font-size: 32px;
`;

const ValidatedInputText = styled(ValidatedInput)`
  border-radius: 4px;
  border: solid 1px #36443A;
`;

const InputLabel = styled(Label)`
  padding: 0.5em 0 0.5em 0em;
`;

const Form = styled(ValidatorForm)`
  display: block;
  margin: 7vh auto 3vh auto;
  ${(p) => p.theme.min('sm')`
    width: 320px;
  `}
  ${(p) => p.theme.max('sm')`
    padding: 0 calc(${(p) => p.theme.layout.mobilePadding} * 2);
  `}
`;

const SubmitButtonCont = styled.div`
  text-align: center;
  margin-top: 8vh;
  ${(p) => p.theme.max('md')`
    margin-top: 14vh;
  `}
`;

const SignInButton = styled(Button)`
  position: relative;
  width: 80%;
  padding-left: 0;
  padding-right: 0;
  border-radius: 4px;
  border-color: ${(p) => p.theme.colors.green.light};
  background: ${(p) => p.theme.colors.green.light};
  ${(p) => p.theme.max('md')`
    width: 90%;
    color: ${(p) => p.theme.colors.primary};
    text-transform: uppercase;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 10%;
  `}
`;

const PasswordInputWrap = styled.div`
  position: relative;
`;

const ForgotLink = styled(RouterLink)`
  font-size: 12px;
  padding: 0 calc(2vw);
  font-weight: 400;
  font-size: 16px;
  text-decoration: underline;
  text-decoration-style: solid;
  display: flex;
  justify-content: center;
`;

const CreateAccountLink = styled(RouterLink)`
  margin-top: 20px;
  font-size: 12px;
  padding: 0 calc(2vw);
  text-align: center;
  font-weight: 400;
  font-size: 16px;
  text-decoration: underline;
  text-decoration-style: solid;
  display: flex;
  justify-content: center;
`;

export default LoginPage;
